/**
 * avatars/animals/build-quadruped.js
 * Xây dựng cây phân cấp 3D cho Động vật 4 chân (Quadruped Archetype)
 * Tách biệt hoàn toàn khỏi blocky-animal.js để tuân thủ quy tắc file < 500 dòng.
 */

import * as THREE from 'three';
import { generateAnimalTexture } from '../animal-presets.js';
import { createAnimalMaterials } from './animal-materials.js';
import { buildEyes, buildBlush, buildEars, buildHorns, buildTail } from './animal-parts.js';

export function buildQuadruped(root, morph, variant, parts, disposables) {
  const m = morph;
  const v = variant;

  // Khởi tạo toàn bộ materials theo từng phân vùng (thân 6 mặt, mặt sạch, tai, chân, đuôi, sừng, bờm)
  const mats = createAnimalMaterials(m, v, disposables, false);

  // --- 1. Thân mình (Body) - Đầm chắc sát sàn ---
  const bodyY = m.legs.height + m.body.height / 2;
  const bodyGroup = new THREE.Group();
  bodyGroup.name = 'BodyGroup';
  bodyGroup.position.set(0, bodyY, 0);
  root.add(bodyGroup);
  parts.body = bodyGroup;

  const bodyGeo = new THREE.BoxGeometry(m.body.width, m.body.height, m.body.length);
  const bodyMesh = new THREE.Mesh(bodyGeo, mats.bodyMaterials);
  bodyMesh.castShadow = false;
  bodyMesh.receiveShadow = true;
  bodyGroup.add(bodyMesh);
  disposables.push(bodyGeo);

  // Lớp len phồng bọc quanh thân cho Cừu (Wool Layer)
  if (m.hasWoolLayer) {
    const extra = (m.woolThickness || 0.05) * 2;
    const woolGeo = new THREE.BoxGeometry(m.body.width + extra, m.body.height + extra, m.body.length + extra);
    const woolCanvas = generateAnimalTexture('wool_fluff', { baseColor: v.baseColor });
    let woolMat;
    if (woolCanvas) {
      const woolTex = new THREE.CanvasTexture(woolCanvas);
      woolTex.colorSpace = THREE.SRGBColorSpace;
      woolMat = new THREE.MeshStandardMaterial({ map: woolTex, roughness: 0.95 });
      disposables.push(woolTex, woolMat);
    } else {
      woolMat = new THREE.MeshStandardMaterial({ color: v.baseColor, roughness: 0.95 });
      disposables.push(woolMat);
    }
    const woolMesh = new THREE.Mesh(woolGeo, woolMat);
    bodyGroup.add(woolMesh);
    disposables.push(woolGeo);
  }

  // Bầu sữa cho Bò (Udder)
  if (m.hasUdder) {
    const udderGeo = new THREE.BoxGeometry(m.body.width * 0.45, 0.08, m.body.length * 0.25);
    const udderMat = new THREE.MeshStandardMaterial({ color: '#f472b6', roughness: 0.7 });
    const udderMesh = new THREE.Mesh(udderGeo, udderMat);
    udderMesh.position.set(0, -m.body.height / 2 - 0.03, -m.body.length * 0.18);
    bodyGroup.add(udderMesh);
    disposables.push(udderGeo, udderMat);
  }

  // --- 2. Đầu (Head) - Gắn trực tiếp vào mặt trước trên thân mình ---
  const headGroup = new THREE.Group();
  headGroup.name = 'HeadGroup';
  const headY = m.body.height * 0.16;
  const headZ = m.body.length / 2;
  headGroup.position.set(0, headY, headZ);
  bodyGroup.add(headGroup);
  parts.head = headGroup;

  const headGeo = new THREE.BoxGeometry(m.head.sizeX, m.head.sizeY, m.head.sizeZ);
  const headMesh = new THREE.Mesh(headGeo, mats.headMaterials);
  headMesh.position.set(0, m.head.sizeY * 0.3, m.head.sizeZ / 2);
  headGroup.add(headMesh);
  disposables.push(headGeo);

  // Bờm ngựa dọc đỉnh đầu & gáy (Horse Mane)
  if (m.hasMane && m.mane) {
    // Đoạn bờm trên đỉnh đầu
    const maneGeo = new THREE.BoxGeometry(m.mane.thickness, m.mane.height, m.head.sizeZ * 0.85);
    const maneMesh = new THREE.Mesh(maneGeo, mats.maneMat);
    maneMesh.position.set(0, m.head.sizeY * 0.3 + m.head.sizeY / 2 + m.mane.height / 2, m.head.sizeZ * 0.35);
    headGroup.add(maneMesh);

    // Đoạn bờm kéo dài xuống gáy/lưng trước
    const maneBackGeo = new THREE.BoxGeometry(m.mane.thickness * 1.1, m.mane.height * 0.9, m.body.length * 0.25);
    const maneBackMesh = new THREE.Mesh(maneBackGeo, mats.maneMat);
    maneBackMesh.position.set(0, m.body.height / 2 + (m.mane.height * 0.9) / 2, m.body.length * 0.35);
    bodyGroup.add(maneBackMesh);

    disposables.push(maneGeo, maneBackGeo);
  }

  // --- 3. Mõm 3D & Mũi ---
  const snoutGeo = new THREE.BoxGeometry(m.snout.width, m.snout.height, m.snout.length);
  const snoutMesh = new THREE.Mesh(snoutGeo, mats.snoutMat);
  const snoutY = m.head.sizeY * 0.18;
  const snoutZ = m.head.sizeZ + m.snout.length / 2;
  snoutMesh.position.set(0, snoutY, snoutZ);
  headGroup.add(snoutMesh);
  disposables.push(snoutGeo);

  // Mũi 3D (Nose)
  const noseW = m.snout.width * 0.4;
  const noseH = m.snout.height * 0.35;
  const noseGeo = new THREE.BoxGeometry(noseW, noseH, 0.035);
  const noseMesh = new THREE.Mesh(noseGeo, mats.noseMat);
  noseMesh.position.set(0, snoutY + m.snout.height * 0.15, m.head.sizeZ + m.snout.length + 0.015);
  headGroup.add(noseMesh);
  disposables.push(noseGeo);

  // Lỗ mũi cho Heo (Pig Nostrils)
  if (m.snout.hasNostrils) {
    const nostrilGeo = new THREE.BoxGeometry(0.04, 0.05, 0.02);
    const nLeft = new THREE.Mesh(nostrilGeo, mats.noseMat);
    nLeft.position.set(-0.045, snoutY, m.head.sizeZ + m.snout.length + 0.018);
    const nRight = new THREE.Mesh(nostrilGeo, mats.noseMat);
    nRight.position.set(0.045, snoutY, m.head.sizeZ + m.snout.length + 0.018);
    headGroup.add(nLeft, nRight);
    disposables.push(nostrilGeo);
  }

  // --- 4. Mắt & Má hồng ---
  buildEyes(headGroup, m, v, mats, disposables, false);
  buildBlush(headGroup, m, mats, disposables, false);

  // --- 5. Tai & Sừng ---
  buildEars(headGroup, m, mats, disposables, parts, v);
  buildHorns(headGroup, m, mats, disposables);

  // --- 6. Bốn Chân (Stubby Legs) ---
  const legH = m.legs.height;
  const legW = m.legs.width;
  const legD = m.legs.depth;
  const legGeo = new THREE.BoxGeometry(legW, legH, legD);
  disposables.push(legGeo);

  const halfSpacingX = m.legs.spacingX / 2;
  const halfSpacingZ = m.legs.spacingZ / 2;

  const createLeg = (name, x, z) => {
    const legJoint = new THREE.Group();
    legJoint.name = name;
    legJoint.position.set(x, -m.body.height / 2, z);
    const legMesh = new THREE.Mesh(legGeo, mats.legMat);
    legMesh.position.y = -legH / 2;
    legJoint.add(legMesh);
    bodyGroup.add(legJoint);
    return legJoint;
  };

  parts.frontLeftLeg = createLeg('FrontLeftLeg', -halfSpacingX, halfSpacingZ);
  parts.frontRightLeg = createLeg('FrontRightLeg', halfSpacingX, halfSpacingZ);
  parts.backLeftLeg = createLeg('BackLeftLeg', -halfSpacingX, -halfSpacingZ);
  parts.backRightLeg = createLeg('BackRightLeg', halfSpacingX, -halfSpacingZ);

  // --- 7. Đuôi (Tail) ---
  buildTail(bodyGroup, m, v, mats, disposables, parts);
}
